import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import {
  FiArrowRight,
  FiCheck,
  FiDownload,
  FiEdit2,
  FiInfo,
  FiUser,
} from "react-icons/fi";
import { LuFileText } from "react-icons/lu";
import { clearTransferSession } from "./api/payoutMutationHelpers";

const formatINR = (amount: number) =>
  `\u20B9 ${amount.toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

type TransferDraft = {
  account: {
    title: string;
    description?: string;
  };
  beneficiary: {
    title: string;
    description?: string;
  };
  purpose: string;
  category: string;
  method: string;
  scheduledFor: string;
  amount: number;
  remarks: string;
  uploadedFile: {
    name: string;
    size: number;
  } | null;
};

const defaultDraft: TransferDraft = {
  account: {
    title: "-",
    description: "-",
  },
  beneficiary: {
    title: "-",
    description: "-",
  },
  purpose: "-",
  category: "-",
  method: "-",
  scheduledFor: "-",
  amount: 0,
  remarks: "",
  uploadedFile: null,
};

const readTransferDraft = () => {
  try {
    const storedDraft = sessionStorage.getItem("payoutTransferDraft");
    return storedDraft ? (JSON.parse(storedDraft) as TransferDraft) : defaultDraft;
  } catch {
    return defaultDraft;
  }
};

function FlowStep({
  index,
  label,
  active,
  complete,
}: {
  index: number;
  label: string;
  active?: boolean;
  complete?: boolean;
}) {
  return (
    <div className="flex items-center gap-3">
      <span
        className={
          "flex h-8 w-8 items-center justify-center rounded-full text-sm font-semibold " +
          (complete
            ? "bg-[#1B163B] text-white"
            : active
              ? "bg-[#1B163B] text-white"
              : "border border-[#D6DCEB] bg-white text-[#505E8E]")
        }
      >
        {complete ? <FiCheck /> : index}
      </span>
      <span
        className={
          "text-sm font-semibold " +
          (active ? "text-[#1B163B]" : "text-[#505E8E]")
        }
      >
        {label}
      </span>
    </div>
  );
}

function DetailRow({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="grid gap-4 border-b border-[#E0E5F2] px-5 py-4 md:grid-cols-[190px_minmax(0,1fr)]">
      <span className="text-sm font-semibold text-[#1B163B]">{label}</span>
      <div className="text-sm font-medium text-[#1B163B]">{children}</div>
    </div>
  );
}

function ApprovalWorkflow() {
  const steps = [
    ["M", "Maker", "-", "Request Created", "Completed"],
    ["C", "Checker", "-", "Pending", "Pending"],
    ["A", "Approver", "-", "Pending", "Pending"],
  ];

  return (
    <div className="rounded-[8px] border border-[#E0E5F2] bg-[#F8FAFF] p-5">
      <h3 className="mb-5 text-sm font-semibold text-[#1B163B]">
        Approval Workflow
      </h3>
      <div className="flex flex-wrap items-start justify-around gap-5">
        {steps.map(([stage, title, helper, sub, status], index) => (
          <React.Fragment key={stage}>
            <div className="text-center">
              <span
                className={
                  "mx-auto flex h-11 w-11 items-center justify-center rounded-full text-lg " +
                  (stage === "M"
                    ? "bg-[#DCFCE7] text-[#16A34A]"
                    : stage === "C"
                      ? "bg-[#FEF3C7] text-[#F59E0B]"
                      : "bg-[#EDE9FE] text-[#6D28D9]")
                }
              >
                {stage === "M" ? <FiCheck /> : <FiUser />}
              </span>
              <p className="mt-3 text-sm font-semibold text-[#1B163B]">
                {title}
              </p>
              <p className="mt-1 text-xs font-medium text-[#505E8E]">
                {helper}
              </p>
              <p
                className={
                  "mt-2 text-xs font-semibold " +
                  (status === "Completed" ? "text-[#16A34A]" : "text-[#F59E0B]")
                }
              >
                {sub}
              </p>
            </div>
            {index < steps.length - 1 && (
              <span className="mt-5 hidden text-xl text-[#505E8E] md:block">
                {"->"}
              </span>
            )}
          </React.Fragment>
        ))}
      </div>
      <p className="mt-6 text-xs font-medium text-[#505E8E]">
        You will be able to track the status in Pending Approval.
      </p>
    </div>
  );
}

function PayoutReviewTransfer() {
  const draft = useMemo(readTransferDraft, []);
  const transferDate =
    draft.scheduledFor && draft.scheduledFor !== "Now"
      ? draft.scheduledFor
      : "Immediate";

  return (
    <div className="w-full px-4 pt-4 pb-8">
      <div className="mb-7">
        <h1 className="text-[22px] font-semibold leading-7 text-[#1B163B]">
          Review Transfer
        </h1>
        <p className="mt-1 text-sm font-medium text-[#505E8E]">
          Please review the transfer details before proceeding.
        </p>
      </div>

      <div className="mb-6 flex flex-wrap items-center gap-10">
        <FlowStep index={1} label="Enter Details" complete />
        <span className="h-px w-20 bg-[#D6DCEB]" />
        <FlowStep index={2} label="Review" active />
        <span className="h-px w-20 bg-[#D6DCEB]" />
        <FlowStep index={3} label="Verify" />
        <span className="h-px w-20 bg-[#D6DCEB]" />
        <FlowStep index={4} label="Confirm" />
      </div>

      <section className="max-w-[760px] rounded-[8px] border border-[#E0E5F2] bg-white p-5 new-shadow">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-[18px] font-semibold text-[#1B163B]">
            Transfer Summary
          </h2>
          <Link
            to="/payouts/transfers/initiate"
            className="flex h-9 items-center gap-2 rounded-[6px] border border-[#1B163B] px-4 text-sm font-semibold text-[#1B163B]"
          >
            <FiEdit2 />
            Edit
          </Link>
        </div>

        <div className="overflow-hidden rounded-[8px] border border-[#E0E5F2]">
          <DetailRow label="Source Account">
            {draft.account.title}
            <p className="mt-1 text-xs text-[#505E8E]">
              {draft.account.description || "-"}
            </p>
          </DetailRow>
          <DetailRow label="Transfer Type">
            <span className="mr-3">{draft.method}</span>
            {draft.method === "IMPS" && (
              <span className="rounded-[5px] bg-[#DCFCE7] px-2 py-1 text-xs font-semibold text-[#16A34A]">
                Instant
              </span>
            )}
          </DetailRow>
          <DetailRow label="Transfer Date">{transferDate}</DetailRow>
          <DetailRow label="Beneficiary">
            <div className="flex items-center gap-3">
              <span className="flex h-9 w-9 items-center justify-center rounded-full bg-[#F4F7FE] text-[#505E8E]">
                <FiUser />
              </span>
              <span>
                <span className="block font-semibold">{draft.beneficiary.title}</span>
                <span className="mt-1 block text-xs text-[#505E8E]">
                  {draft.beneficiary.description || "-"}
                </span>
              </span>
            </div>
          </DetailRow>
          <DetailRow label="Amount">
            <span className="font-semibold">{formatINR(draft.amount)}</span>
            <p className="mt-1 text-xs text-[#505E8E]">
              Amount entered for this payout transfer
            </p>
          </DetailRow>
          <DetailRow label="Category">
            <span className="rounded-[5px] bg-[#EDE9FE] px-2 py-1 text-xs font-semibold text-[#6D28D9]">
              {draft.category}
            </span>
          </DetailRow>
          <DetailRow label="Reference / Remark">
            {draft.remarks || "-"}
          </DetailRow>
          <DetailRow label="Charges">
            {formatINR(0)}
            <span className="ml-3 rounded-[5px] bg-[#DCFCE7] px-2 py-1 text-xs font-semibold text-[#16A34A]">
              No charges
            </span>
          </DetailRow>
          <DetailRow label="Total Debit Amount">
            <span className="font-semibold">{formatINR(draft.amount)}</span>
          </DetailRow>
        </div>

        <div className="mt-5 flex items-center gap-3 rounded-[6px] border border-[#F59E0B66] bg-[#FEF3C7] px-4 py-3 text-sm font-medium text-[#92400E]">
          <FiInfo />
          This transfer will be processed after approval as per the configured
          workflow.
        </div>

        {draft.uploadedFile && (
        <div className="mt-5">
          <h3 className="mb-3 text-sm font-semibold text-[#1B163B]">
            Attachments (1)
          </h3>
          <div className="flex max-w-[280px] items-center justify-between rounded-[6px] border border-[#E0E5F2] bg-white px-4 py-3">
            <div className="flex items-center gap-3">
              <span className="flex h-9 w-9 items-center justify-center rounded-[6px] bg-[#FEE2E2] text-[#EF4444]">
                <LuFileText />
              </span>
              <span>
                <span className="block text-xs font-semibold text-[#1B163B]">
                  {draft.uploadedFile.name}
                </span>
                <span className="text-xs font-medium text-[#505E8E]">
                  {draft.uploadedFile.size} KB
                </span>
              </span>
            </div>
            <FiDownload className="text-[#505E8E]" />
          </div>
        </div>
        )}

        <div className="mt-5">
          <ApprovalWorkflow />
        </div>

        <div className="mt-6 flex flex-wrap items-center justify-between gap-4">
          <div className="flex flex-wrap items-center gap-3">
            <Link
              to="/payouts/transfers/initiate"
              className="flex h-10 items-center rounded-[6px] border border-[#E0E5F2] px-8 text-sm font-semibold text-[#1B163B]"
            >
              Back
            </Link>
            <Link
              to="/payouts/transfers"
              onClick={clearTransferSession}
              className="flex h-10 items-center rounded-[6px] border border-[#E0E5F2] bg-white px-8 text-sm font-semibold text-[#1B163B]"
            >
              Cancel
            </Link>
          </div>
          <div className="flex items-center">
            <Link
              to="/payouts/transfers/initiate/verify"
              className="flex h-10 items-center gap-3 rounded-[6px] bg-[#1B163B] px-7 text-sm font-semibold text-white"
            >
              Continue to Verification
              <FiArrowRight />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}

export default PayoutReviewTransfer;
